import { ActionIcon, Select } from '@mantine/core';
import { ArrowDown, Star, Trash2 } from 'lucide-react';

import Icon from '@/components/Icon.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useAutoSelectSoleOption } from '@/hooks/use-auto-select-sole-option.ts';

import { type MergedGap } from '@/models/appointment/AppointmentChainConfig.ts';

import { formatGapRange } from '@/utils/duration-utils.ts';
import { cn } from '@/utils/styleUtils.ts';

interface Props {
    chainId: string | undefined;
    stepOrder: number;
    appointmentTypeName: string;
    durationMinutes: number | undefined;
    calendarOptions: { value: string; label: string }[];
    selectedCalendarId: string | undefined;
    isMarked: boolean;
    isRemovable: boolean;
    effectiveGap: MergedGap | undefined;
    onCalendarChange: (calendarId: string | undefined) => void;
    onRemove: () => void;
}

export const ChainStepRow = ({
    chainId,
    stepOrder,
    appointmentTypeName,
    durationMinutes,
    calendarOptions,
    selectedCalendarId,
    isMarked,
    isRemovable,
    effectiveGap,
    onCalendarChange,
    onRemove,
}: Props) => {
    const handleCalendarChange = (value: string | null): void => {
        onCalendarChange(value ?? undefined);
    };

    useAutoSelectSoleOption({
        options: calendarOptions,
        getKey: (option) => option.value,
        hasSelection: selectedCalendarId !== undefined,
        onSelect: (option) => onCalendarChange(option.value),
    });

    const gapLabel = effectiveGap ? formatGapRange(effectiveGap.min_duration, effectiveGap.max_duration, 'de') : '';

    return (
        <div>
            <div
                className={cn(
                    'rounded-lg border bg-white p-4 shadow-sm dark:bg-neutral-700',
                    isMarked && 'border-amber-200 bg-amber-50 dark:bg-amber-950'
                )}
            >
                <div className="flex items-center gap-3">
                    <div className="flex flex-1 flex-col">
                        <Paragraph className="flex items-center gap-1.5 text-sm font-medium">
                            {isMarked && <Icon icon={Star} size="size-4" color="fill-amber-400 text-amber-400" />}
                            {appointmentTypeName}
                        </Paragraph>
                        <Paragraph className="text-xs text-slate-500">{durationMinutes ?? '–'} Min.</Paragraph>
                    </div>
                    <Select
                        key={`${chainId}-${stepOrder}`}
                        className="w-48"
                        size="sm"
                        placeholder="Kalender"
                        searchable
                        data={calendarOptions}
                        // eslint-disable-next-line unicorn/no-null -- Mantine Select needs `null` (not `undefined`) to represent an empty/cleared value
                        value={selectedCalendarId ?? null}
                        onChange={handleCalendarChange}
                        allowDeselect={false}
                        error={selectedCalendarId ? undefined : 'Bitte auswählen'}
                    />
                    <ActionIcon
                        variant="subtle"
                        color="red"
                        size="lg"
                        onClick={onRemove}
                        disabled={!isRemovable}
                        aria-label="Schritt entfernen"
                    >
                        <Icon icon={Trash2} size="size-4" />
                    </ActionIcon>
                </div>
            </div>

            {effectiveGap && (
                <div className="flex items-center gap-3 py-2 pl-3">
                    <div className="flex w-7 shrink-0 flex-col items-center">
                        <div className="h-3 w-0.5 bg-primary/30" />
                        <Icon icon={ArrowDown} size="size-4" color="text-primary/50" />
                        <div className="h-3 w-0.5 bg-primary/30" />
                    </div>
                    {gapLabel && (
                        <div className="border-border/50 flex items-center gap-2 rounded-full border bg-slate-50 px-3 py-1.5 text-xs dark:bg-neutral-600">
                            <span className="whitespace-nowrap text-slate-500">Zeitlicher Abstand: {gapLabel}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
